// src/components/dashboard/ManualLeadUploadModal.tsx
// Final Version: Inserts into outbound_call_queue with manual_upload_instruction
// Updated: Flexible US phone number parsing and normalization to E.164 format.
// Refined: PapaParse error typing and minor comment correction.
// Fixed: Corrected Papa.parse error callback signature to resolve TS2769.

import { useState, ChangeEvent, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useBusinessName, usePreCallEmailsEnabled } from '@/hooks/useEmailQueries';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, UploadCloud, UserPlus, FileText, CheckCircle2, AlertCircle, MessageSquareText } from 'lucide-react';
import { INSTRUCTION_MIN_LENGTH, INSTRUCTION_MAX_LENGTH, MAX_LEADS_PER_UPLOAD, MAX_FILE_SIZE_MB, SingleLeadFormData, singleLeadSchema, parseCsvFile, validateInstruction, submitLeads, downloadTemplate } from './services';
import Instructions from './Instructions';
import { ManualLead } from '@/types/manualLeadUpload';


export function ManualLeadUploadModal({ isOpen, onOpenChange }: { isOpen: boolean, onOpenChange: (isOpen: boolean) => void }) {
    const { user } = useAuth();
    const { data: businessName } = useBusinessName();
    const { data: isPreCallEmailsEnabled = false } = usePreCallEmailsEnabled();
    
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("single");
    const [csvFileName, setCsvFileName] = useState<string | null>(null);
    const [parsedLeads, setParsedLeads] = useState<ManualLead[]>([]);
    const [parseErrors, setParseErrors] = useState<string[]>([]);
    const [manualCallInstruction, setManualCallInstruction] = useState<string>('');

    const {
        register: registerSingle,
        handleSubmit: handleSingleSubmit,
        formState: { errors: singleErrors },
        reset: resetSingleForm,
        setValue: setSingleValue, 
        trigger: triggerSingle,   
    } = useForm<SingleLeadFormData>({
        resolver: zodResolver(singleLeadSchema),
        defaultValues: { name: '', phone_number: '', email: '', manual_call_instruction: '' }
    });

    const resetCsvState = () => {
        setCsvFileName(null);
        setParsedLeads([]);
        setParseErrors([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; 
        }
    };

    const handleModalOpenChange = (open: boolean) => {
        if (!open) {
            resetSingleForm({ name: '', phone_number: '', email: '', manual_call_instruction: '' });
            resetCsvState();
            setManualCallInstruction('');
            setActiveTab("single");
            setIsSubmitting(false);
        }
        onOpenChange(open);
    };
    
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setParsedLeads([]);
        setParseErrors([]);

        const file = event.target.files?.[0];

        if (file) {
            if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
                toast.error('Invalid file type. Please upload a CSV file.');
                setCsvFileName(null);
                if (fileInputRef.current) { 
                    fileInputRef.current.value = '';
                }
                return;
            }
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                 toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
                 setCsvFileName(null);
                 if (fileInputRef.current) { 
                    fileInputRef.current.value = '';
                 }
                 return;
            }

            setCsvFileName(file.name);
            parseCsvFile(file, setIsSubmitting, setParseErrors, setParsedLeads, toast, isPreCallEmailsEnabled);
        } else {
            console.warn("handleFileChange: No file selected or event.target.files is empty.");
            setCsvFileName(null); 
        }
    };


    const onSingleSubmit = (data: SingleLeadFormData) => {
        // data.phone_number is already normalized by Zod transform
        submitLeads([{ phone_number: data.phone_number, name: data.name || null, email: data.email || null }], user, setIsSubmitting, handleModalOpenChange, businessName, isPreCallEmailsEnabled, manualCallInstruction, supabase);
    };

    const onCsvSubmit = async () => {
         if (parsedLeads.length > 0) {
            if (validateInstruction(manualCallInstruction)) { 
                submitLeads(parsedLeads, user, setIsSubmitting,  handleModalOpenChange, businessName, isPreCallEmailsEnabled, manualCallInstruction, supabase);
            }
         } else {
             toast.warning("No valid leads were parsed from the CSV to submit.");
         }
    };
    const triggerFileInput = () => fileInputRef.current?.click();

    return (
        <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <div className="flex items-center space-x-2">
                        <MessageSquareText className="h-6 w-6 text-primary"/>
                        <DialogTitle>Upload Leads for Custom AI Calls</DialogTitle>
                    </div>
                    <DialogDescription>
                        Add leads and specify the AI's objective for each call. Provide US phone numbers (e.g., (555) 123-4567, +15551234567).
                    </DialogDescription>
                </DialogHeader>

                 <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full pt-2">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="single" disabled={isSubmitting}><UserPlus className="w-4 h-4 mr-2"/>Single Lead</TabsTrigger>
                        <TabsTrigger value="bulk" disabled={isSubmitting}><UploadCloud className="w-4 h-4 mr-2"/>Upload CSV</TabsTrigger>
                    </TabsList>

                    <TabsContent value="single" className="pt-6">
                         <form onSubmit={handleSingleSubmit(onSingleSubmit)} className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="single-name">Name (Optional)</Label>
                                <Input
                                    id="single-name"
                                    {...registerSingle('name')}
                                    placeholder="e.g., Jane Doe"
                                    className="mt-1"
                                    disabled={isSubmitting}
                                />
                                {singleErrors.name && <p className="text-red-600 text-xs mt-1">{singleErrors.name.message}</p>}
                            </div>
                             <div className="space-y-1.5">
                                <Label htmlFor="single-phone">Phone Number</Label>
                                <Input
                                    id="single-phone"
                                    {...registerSingle('phone_number')}
                                    placeholder="(555) 123-4567" // Updated placeholder
                                    className="mt-1"
                                    disabled={isSubmitting}
                                    type="tel"
                                />
                                {singleErrors.phone_number && <p className="text-red-600 text-xs mt-1">{singleErrors.phone_number.message}</p>}
                            </div>
                            
                            {isPreCallEmailsEnabled && (
                                <div className="space-y-1.5 col-span-1">
                                    <Label htmlFor="single-email">Email (Optional)</Label>
                                    <Input
                                        id="single-email"
                                        {...registerSingle('email')}
                                        placeholder="john@example.com"
                                        className="mt-1"
                                        disabled={isSubmitting}
                                        type="email"
                                    />
                                    {singleErrors.email && <p className="text-red-600 text-xs mt-1">{singleErrors.email.message}</p>}
                                    <p className="text-xs text-muted-foreground">Email for pre-call notification (recommended)</p>
                                </div>
                            )}
                            
                            <Instructions 
                                manualCallInstruction={manualCallInstruction}
                                setManualCallInstruction={setManualCallInstruction}
                                activeTab={activeTab}
                                isSubmitting={isSubmitting}
                                parsedLeads={parsedLeads}
                                singleErrors={singleErrors}
                                setSingleValue={setSingleValue}
                                triggerSingle={triggerSingle}
                            />

                             <DialogFooter className="pt-4 col-span-2">
                                <DialogClose asChild>
                                     <Button type="button" variant="outline" disabled={isSubmitting}>Cancel</Button>
                                </DialogClose>
                                <Button type="submit" disabled={isSubmitting || !!singleErrors.manual_call_instruction || !!singleErrors.phone_number}>
                                    {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding to Queue...</> : 'Add Lead to Queue'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </TabsContent>

                    <TabsContent value="bulk" className="pt-6 space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                            <Instructions 
                                manualCallInstruction={manualCallInstruction}
                                setManualCallInstruction={setManualCallInstruction}
                                activeTab={activeTab}
                                isSubmitting={isSubmitting}
                                parsedLeads={parsedLeads}
                                singleErrors={singleErrors}
                                setSingleValue={setSingleValue}
                                triggerSingle={triggerSingle}
                            />
                        </div>

                         <div className="space-y-2">
                            <Label>Upload CSV File</Label>
                             <p className="text-xs text-muted-foreground">
                                Columns: <code>phone_number</code> (required, US formats like (XXX) XXX-XXXX), <code>name</code> (optional),  <code>email</code> (optional). Max {MAX_LEADS_PER_UPLOAD} leads, {MAX_FILE_SIZE_MB}MB limit. Download the template to see the exact format. The instruction above will apply to all leads in this CSV.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                <Button type="button" variant="outline" onClick={triggerFileInput} disabled={isSubmitting}>
                                    <UploadCloud className="mr-2 h-4 w-4" /> {csvFileName ? 'Change File' : 'Choose File'}
                                </Button>
                                 <Button type="button" variant="ghost" size="sm" onClick={() => downloadTemplate(isPreCallEmailsEnabled)} className="text-xs px-2" disabled={isSubmitting}>
                                    <FileText className="mr-1 h-3 w-3"/>Download Template
                                </Button>
                                 <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".csv, text/csv"
                                    className="hidden"
                                    disabled={isSubmitting}
                                />
                                {csvFileName && !isSubmitting && ( 
                                     <span className="text-sm text-muted-foreground truncate max-w-[200px]" title={csvFileName}>
                                         Selected: {csvFileName}
                                     </span>
                                )}
                            </div>
                            {isSubmitting && !parsedLeads.length && !parseErrors.length && csvFileName && ( 
                                <div className="flex items-center text-sm text-muted-foreground mt-2">
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Parsing {csvFileName}...
                                </div>
                            )}
                        </div>

                        {(parsedLeads.length > 0 || parseErrors.length > 0) && !isSubmitting && ( 
                            <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-2 bg-slate-50 dark:bg-slate-800 text-sm shadow-inner">
                                <h4 className="font-medium text-sm mb-1 sticky top-0 bg-slate-50 dark:bg-slate-800 py-1 border-b -mx-3 px-3">
                                    CSV Processing Results:
                                </h4>
                                {parsedLeads.length > 0 && (
                                     <div className="flex items-start text-green-700 dark:text-green-400">
                                        <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0"/>
                                        <span>{parsedLeads.length} valid leads found and ready for submission.</span>
                                    </div>
                                )}
                                {parseErrors.length > 0 && (
                                    <div className="space-y-1">
                                         <div className="flex items-start text-orange-600 dark:text-orange-400 font-medium">
                                             <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0"/>
                                            <span>Validation Results:</span>
                                        </div>
                                        <ul className="list-disc list-inside pl-6 text-xs space-y-0.5">
                                            {parseErrors.map((error, index) => {
                                                let textColor = 'text-orange-700 dark:text-orange-500';
                                                if (error.includes('info:')) {
                                                    textColor = 'text-blue-700 dark:text-blue-400';
                                                } else if (error.includes('warning:')) {
                                                    textColor = 'text-yellow-700 dark:text-yellow-500';
                                                } else if (error.includes('error:')) {
                                                    textColor = 'text-red-700 dark:text-red-500';
                                                }
                                                return (
                                                    <li key={index} className={textColor}>{error}</li>
                                                );
                                            })}
                                        </ul>
                                     </div>
                                )}
                            </div>
                        )}

                        <DialogFooter className="pt-4">
                            <DialogClose asChild>
                                <Button type="button" variant="outline" disabled={isSubmitting}>Cancel</Button>
                            </DialogClose>
                             <Button
                                type="button"
                                onClick={onCsvSubmit}
                                disabled={isSubmitting || parsedLeads.length === 0 || manualCallInstruction.trim().length < INSTRUCTION_MIN_LENGTH || manualCallInstruction.trim().length > INSTRUCTION_MAX_LENGTH}
                                title={parsedLeads.length === 0 ? "Upload a CSV with valid leads first, or check parsing errors above." : manualCallInstruction.trim().length < INSTRUCTION_MIN_LENGTH ? `Instruction too short (min ${INSTRUCTION_MIN_LENGTH})` : manualCallInstruction.trim().length > INSTRUCTION_MAX_LENGTH ? `Instruction too long (max ${INSTRUCTION_MAX_LENGTH})` : `Add ${parsedLeads.length} valid leads to queue`}
                             >
                                {isSubmitting && parsedLeads.length > 0 ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding to Queue...</> : `Add ${parsedLeads.length > 0 ? parsedLeads.length : ''} Lead(s) to Queue`}
                            </Button>
                        </DialogFooter>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}